import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateCoafCommunication, getSuspicionIndicatorLabel, InvolvedParty } from '../../../hooks/useCoaf';
import { ArrowLeft, Save, Plus, Trash2, User, AlertTriangle } from 'lucide-react';

const operationTypes = [
  { value: 'PIX', label: 'PIX' },
  { value: 'TED', label: 'TED' },
  { value: 'DOC', label: 'DOC' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'CASH_DEPOSIT', label: 'Depósito em Espécie' },
  { value: 'CASH_WITHDRAWAL', label: 'Saque em Espécie' },
  { value: 'CRYPTO', label: 'Criptomoeda' },
  { value: 'OTHER', label: 'Outro' },
];

const suspicionIndicators = [
  'HIGH_VALUE',
  'STRUCTURING',
  'UNUSUAL_PATTERN',
  'GEOGRAPHIC_RISK',
  'PEP_INVOLVEMENT',
  'SHELL_COMPANY',
  'RAPID_MOVEMENT',
  'INCONSISTENT_PROFILE',
  'THIRD_PARTY',
  'OTHER',
];

const partyTypes = [
  { value: 'SENDER', label: 'Remetente' },
  { value: 'RECEIVER', label: 'Destinatário' },
  { value: 'BENEFICIARY', label: 'Beneficiário' },
  { value: 'OTHER', label: 'Outro' },
];

const documentTypes = [
  { value: 'CPF', label: 'CPF' },
  { value: 'CNPJ', label: 'CNPJ' },
  { value: 'PASSPORT', label: 'Passaporte' },
  { value: 'OTHER', label: 'Outro' },
];

const emptyParty: InvolvedParty = {
  type: 'SENDER',
  name: '',
  document: '',
  documentType: 'CPF',
  role: '',
};

export default function CoafCommunicationCreate() {
  const navigate = useNavigate();
  const createMutation = useCreateCoafCommunication();
  
  const [formData, setFormData] = useState({
    type: 'MANUAL',
    operationType: 'PIX',
    operationDate: new Date().toISOString().split('T')[0],
    operationValue: 0,
    operationDescription: '',
    involvedParties: [{ ...emptyParty }] as InvolvedParty[],
    suspicionIndicators: [] as string[],
    additionalInfo: '',
  });

  const handleAddParty = () => {
    setFormData(prev => ({
      ...prev,
      involvedParties: [...prev.involvedParties, { ...emptyParty }],
    }));
  };

  const handleRemoveParty = (index: number) => {
    setFormData(prev => ({
      ...prev,
      involvedParties: prev.involvedParties.filter((_, i) => i !== index),
    }));
  };

  const handlePartyChange = (index: number, field: keyof InvolvedParty, value: string) => {
    setFormData(prev => ({
      ...prev,
      involvedParties: prev.involvedParties.map((party, i) =>
        i === index ? { ...party, [field]: value } : party
      ),
    }));
  };

  const handleIndicatorToggle = (indicator: string) => {
    setFormData(prev => ({
      ...prev,
      suspicionIndicators: prev.suspicionIndicators.includes(indicator)
        ? prev.suspicionIndicators.filter(i => i !== indicator)
        : [...prev.suspicionIndicators, indicator],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const result = await createMutation.mutateAsync(formData);
      navigate(`/admin/compliance/coaf/${result.id}`);
    } catch (err) {
      console.error('Erro ao criar comunicação:', err);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/compliance/coaf/list')}
          className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">Nova Comunicação COAF</h1>
          <p className="text-surface-400">Criar comunicação manual ao COAF</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Operation Info */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4">Informações da Operação</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Tipo de Operação</label>
              <select
                value={formData.operationType}
                onChange={(e) => setFormData(prev => ({ ...prev, operationType: e.target.value }))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              >
                {operationTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Data da Operação</label>
              <input
                type="date"
                value={formData.operationDate}
                onChange={(e) => setFormData(prev => ({ ...prev, operationDate: e.target.value }))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Valor (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.operationValue}
                onChange={(e) => setFormData(prev => ({ ...prev, operationValue: Number(e.target.value) }))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2">Descrição da Operação</label>
            <textarea
              value={formData.operationDescription}
              onChange={(e) => setFormData(prev => ({ ...prev, operationDescription: e.target.value }))}
              rows={3}
              className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Descreva detalhadamente a operação suspeita..."
              required
            />
          </div>
        </div>

        {/* Involved Parties */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" />
              Partes Envolvidas
            </h2>
            <button
              type="button"
              onClick={handleAddParty}
              className="flex items-center gap-2 px-3 py-1 bg-surface-700 hover:bg-surface-600 rounded-lg text-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Adicionar
            </button>
          </div>
          
          <div className="space-y-4">
            {formData.involvedParties.map((party, index) => (
              <div key={index} className="p-4 bg-surface-700/50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium">Parte {index + 1}</span>
                  {formData.involvedParties.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveParty(index)}
                      className="p-1 hover:bg-red-500/20 rounded text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-surface-400 mb-1">Tipo</label>
                    <select
                      value={party.type}
                      onChange={(e) => handlePartyChange(index, 'type', e.target.value)}
                      className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-sm"
                    >
                      {partyTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-surface-400 mb-1">Nome</label>
                    <input
                      type="text"
                      value={party.name}
                      onChange={(e) => handlePartyChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-sm"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-surface-400 mb-1">Tipo de Documento</label>
                    <select
                      value={party.documentType}
                      onChange={(e) => handlePartyChange(index, 'documentType', e.target.value)}
                      className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-sm"
                    >
                      {documentTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-surface-400 mb-1">Documento</label>
                    <input
                      type="text"
                      value={party.document}
                      onChange={(e) => handlePartyChange(index, 'document', e.target.value)}
                      className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-sm"
                      required
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm text-surface-400 mb-1">Papel na Operação</label>
                    <input
                      type="text"
                      value={party.role}
                      onChange={(e) => handlePartyChange(index, 'role', e.target.value)}
                      className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-sm"
                      placeholder="Ex: Ordenante, Beneficiário final..."
                      required
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Suspicion Indicators */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            Indicadores de Suspeição
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {suspicionIndicators.map((indicator) => (
              <button
                key={indicator}
                type="button"
                onClick={() => handleIndicatorToggle(indicator)}
                className={`p-3 rounded-lg border text-left text-sm transition-colors ${
                  formData.suspicionIndicators.includes(indicator)
                    ? 'border-yellow-500 bg-yellow-500/10 text-yellow-400'
                    : 'border-surface-600 hover:border-surface-500'
                }`}
              >
                {getSuspicionIndicatorLabel(indicator)}
              </button>
            ))}
          </div>
          
          {formData.suspicionIndicators.length === 0 && (
            <p className="text-sm text-surface-400 mt-3">
              Selecione pelo menos um indicador de suspeição
            </p>
          )}
        </div>

        {/* Additional Info */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4">Informações Adicionais</h2>
          
          <textarea
            value={formData.additionalInfo}
            onChange={(e) => setFormData(prev => ({ ...prev, additionalInfo: e.target.value }))}
            rows={4}
            className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Informações complementares, contexto, histórico do cliente..."
          />
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={createMutation.isPending || formData.suspicionIndicators.length === 0}
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {createMutation.isPending ? 'Criando...' : 'Criar Comunicação'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/compliance/coaf/list')}
            className="px-6 py-3 bg-surface-700 hover:bg-surface-600 rounded-lg font-medium"
          >
            Cancelar
          </button>
        </div>

        {createMutation.isError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
            Erro ao criar comunicação. Verifique os dados e tente novamente.
          </div>
        )}
      </form>
    </div>
  );
}
